import type { Option } from '@openx/types/options';
import type { FiltersContext } from '../context/types';
import { type ConfigurableFilter, FILTER_TYPES, type FilterCriteria, type FilterType } from './types';
import { isFilterWithOptions } from './utils/filtersTypeGuards';

type GetFilterButtonValueArgs<Criteria extends FilterCriteria> = {
  criteria?: FilterCriteria;
  key: string;
  options?: Option[];
  type?: FilterType;
  filter?: FiltersContext<Criteria>['filtersConfig'][number];
};

export const getFilterButtonValue = <Criteria extends FilterCriteria>({
  criteria,
  key,
  options,
  type,
  filter,
}: GetFilterButtonValueArgs<Criteria>): string => {
  const value = criteria?.[key];

  if (type === FILTER_TYPES.MULTI_OPTION_WITH_RADIO || type === FILTER_TYPES.MULTI_OPTION_WITH_RANGE) {
    const selectedCount =
      options?.reduce((count, option) => {
        if (criteria?.[option.id]) {
          return count + 1;
        }

        return count;
      }, 0) ?? 0;

    if (selectedCount === 0) return '';

    return `Selected ${selectedCount}`;
  }

  if (type === FILTER_TYPES.MULTI_SELECT_WITH_RADIO) {
    let criteriaWithValue: string[] = [];
    let optionName = '';

    options?.forEach(option => {
      if (criteria?.[option.id]) {
        criteriaWithValue = criteria?.[option.id] as string[];
        optionName = option.name;
      }
    });

    return optionName ? `${optionName} ${criteriaWithValue.length > 0 ? `+${criteriaWithValue.length}` : ''}` : '';
  }

  if (type === FILTER_TYPES.RANGE_WITH_RADIO) {
    let rangeValue = '';
    let radioValue = '';

    options?.forEach(option => {
      if (criteria?.[option.id]) {
        if (Array.isArray(criteria?.[option.id])) {
          rangeValue = ((criteria?.[option.id] as string[] | undefined) ?? []).join(' - ');
        } else {
          radioValue =
            filter?.type === FILTER_TYPES.RANGE_WITH_RADIO &&
            (criteria?.[option.id] as string) !== filter?.config?.defaultOption
              ? (criteria?.[option.id] as string)
              : '';
        }
      }
    });

    return rangeValue || radioValue ? `${radioValue ? `${radioValue}` : ''} ${rangeValue} ` : '';
  }

  if (!value) {
    return '';
  }

  if ((type === FILTER_TYPES.TEXT_INPUT || type === FILTER_TYPES.NUMERIC_INPUT) && typeof value === 'string') {
    return value;
  }

  if ((type === FILTER_TYPES.RANGE || type === FILTER_TYPES.DATE_RANGE) && Array.isArray(value)) {
    const [min, max] = value;

    if (min && max) return `${min} - ${max}`;
    if (min) return `> ${min}`;
    if (max) return `< ${max}`;

    return '';
  }

  if (type === FILTER_TYPES.SEARCH_AND_SELECT && typeof value === 'string') {
    const option = options?.find(option => option.id === value);

    return option ? option.name : value;
  }

  if (!Array.isArray(value)) {
    return options?.find(option => option.id === value)?.name || '';
  }

  const selectedOptions = options?.filter(option => value.includes(option.id)) ?? [];

  if (selectedOptions.length <= 1) {
    return selectedOptions[0]?.name || '';
  }

  return `${selectedOptions[0]?.name} +${selectedOptions.length - 1}`;
};

export const filterVisibleFilters = <Criteria extends FilterCriteria>(
  filters: FiltersContext<Criteria>['filtersConfig'],
  visibleFiltersKeys: FiltersContext<Criteria>['visibleFilters']
) => {
  const filtersWithOptions = filters.filter(filter => {
    if (isFilterWithOptions<Criteria>(filter)) {
      return filter.withLoader ? true : 'options' in filter && filter.options.length > 0;
    }

    return true;
  });

  const filteredVisibleFilters: FiltersContext<Criteria>['filtersConfig'] = filtersWithOptions
    .filter(filter => visibleFiltersKeys.includes(filter.key))
    .sort((a, b) => {
      const indexA = visibleFiltersKeys.indexOf(a.key);
      const indexB = visibleFiltersKeys.indexOf(b.key);
      return indexA - indexB;
    });

  const moreFilters = filtersWithOptions.filter(filter => !visibleFiltersKeys.includes(filter.key));

  return {
    filteredVisibleFilters,
    moreFilters,
  };
};

export const countActiveMoreFilters = <Criteria extends FilterCriteria>(
  filters: FiltersContext<Criteria>['filtersConfig'],
  filterValues?: Criteria
): number => {
  if (!filterValues) return 0;

  return filters.reduce((count, filter) => {
    const value = filterValues[filter.key];

    if (filter.type === FILTER_TYPES.MULTI_OPTION_WITH_RANGE || filter.type === FILTER_TYPES.MULTI_OPTION_WITH_RADIO) {
      filter.options.forEach(option => {
        const optionValue = filterValues[option.id];
        if (optionValue !== undefined) {
          count += 1;
        }
      });

      return count;
    }

    if (filter.type === FILTER_TYPES.RADIO) {
      if (filter.defaultOption && filterValues[filter.key] === filter.defaultOption) {
        return count;
      }
    }

    if (!value) return count;

    if (filter.type === FILTER_TYPES.RANGE || filter.type === FILTER_TYPES.DATE_RANGE) {
      return Array.isArray(value) && (value[0] || value[1]) ? count + 1 : count;
    }

    if (Array.isArray(value)) {
      return count + value.length;
    }

    return count + 1;
  }, 0);
};

export const FILTERS_WITH_OPTIONS = [
  FILTER_TYPES.CHECKBOX,
  FILTER_TYPES.DROPDOWN,
  FILTER_TYPES.SWITCH,
  FILTER_TYPES.RADIO,
  FILTER_TYPES.AUTOCOMPLETE,
  FILTER_TYPES.MULTI_OPTION_WITH_RANGE,
  FILTER_TYPES.MULTI_OPTION_WITH_RADIO,
  FILTER_TYPES.MULTI_SELECT_WITH_RADIO,
  FILTER_TYPES.RANGE_WITH_RADIO,
  FILTER_TYPES.SEARCH_AND_SELECT,
] as const;

export type FilterWithOptionsType = (typeof FILTERS_WITH_OPTIONS)[number];

export const getFilterOptions = <T>(filter: ConfigurableFilter<T>): Option[] | undefined => {
  if (FILTERS_WITH_OPTIONS.includes(filter.type as FilterWithOptionsType) && 'options' in filter) {
    return filter.options;
  }

  return undefined;
};
